import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

const loadProfile = async (userId) => {
  const { data } = await supabase
    .from('admin_users')
    .select('id, username, email, full_name, role, is_active, last_login, created_at')
    .eq('id', userId)
    .maybeSingle();
  return data && data.is_active ? { ...data, userType: 'admin' } : null;
};

// A staff member with a verified authenticator (TOTP) must finish a code challenge before the session is
// aal2. The database ignores aal1 sessions of such staff, so this step is enforced server-side too.
// Returns the verified factor id when a code is still needed, otherwise null.
const pendingMfaFactor = async () => {
  const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (!aal || aal.nextLevel !== 'aal2' || aal.currentLevel === 'aal2') return null;
  const { data: factors } = await supabase.auth.mfa.listFactors();
  return factors?.totp?.[0]?.id ?? null;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mfaFactorId, setMfaFactorId] = useState(null);

  // Keep the Supabase session in state. Profile loading happens in a separate
  // effect: awaiting Supabase calls inside this callback can deadlock the client.
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (!data.session) setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      if (!next) {
        setUser(null);
        setMfaFactorId(null);
        setLoading(false);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const userId = session?.user?.id;
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      const factorId = await pendingMfaFactor();
      if (cancelled) return;
      if (factorId) {
        // Restored a password-only session for an MFA user: wait for the code, load no profile yet.
        setMfaFactorId(factorId);
        setLoading(false);
        return;
      }
      const profile = await loadProfile(userId);
      if (cancelled) return;
      setUser(profile);
      setLoading(false);
      // Signed in to Supabase but not an active staff member: drop the session.
      if (!profile) supabase.auth.signOut();
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const loginAdmin = useCallback(async (email, password) => {
    setError(null);
    const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      const message = signInError.message === 'Invalid login credentials'
        ? 'Invalid credentials'
        : signInError.message;
      setError(message);
      return { success: false, message };
    }
    const factorId = await pendingMfaFactor();
    if (factorId) {
      setMfaFactorId(factorId);
      return { success: false, mfaRequired: true };
    }
    const profile = await loadProfile(data.user.id);
    if (!profile) {
      await supabase.auth.signOut();
      const message = 'This account does not have admin access';
      setError(message);
      return { success: false, message };
    }
    setUser(profile);
    return { success: true };
  }, []);

  const verifyMfa = useCallback(async (code) => {
    setError(null);
    if (!mfaFactorId) return { success: false, message: 'No verification is pending' };
    const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
      factorId: mfaFactorId,
      code: code.trim()
    });
    if (verifyError) {
      const message = 'That code is not valid. Check your authenticator app and try again.';
      setError(message);
      return { success: false, message };
    }
    const { data: current } = await supabase.auth.getUser();
    const profile = current?.user ? await loadProfile(current.user.id) : null;
    if (!profile) {
      await supabase.auth.signOut();
      const message = 'This account does not have admin access';
      setError(message);
      return { success: false, message };
    }
    setMfaFactorId(null);
    setUser(profile);
    return { success: true };
  }, [mfaFactorId]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setMfaFactorId(null);
  }, []);

  const value = {
    user,
    loading,
    error,
    loginAdmin,
    verifyMfa,
    mfaRequired: !!mfaFactorId && !user,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.userType === 'admin',
    isSuperAdmin: user?.role === 'super-admin',
    isViewer: user?.role === 'viewer'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
